import type { DocSection } from '../types'
import { FIG_SOFTPHONE } from '../figures'

export const callsSection: DocSection = {
  id: 'appels',
  title: 'Passer & recevoir des appels',
  icon: 'phone',
  accent: '#0EA5E9',
  summary: 'Le softphone, la file, les appels entrants, le transfert, et tout ce qui se passe pendant un appel.',
  minRole: 'agent',
  articles: [
    {
      id: 'softphone',
      title: 'Le softphone (panneau téléphone)',
      summary: 'Composer, décrocher, mute, attente, transfert — depuis le panneau de droite.',
      icon: 'phone',
      blocks: [
        { type: 'p', text: "Le **softphone** est le panneau de droite. C'est votre téléphone : il n'y a aucun appareil à brancher, l'audio passe par votre ordinateur (casque recommandé)." },
        { type: 'figure', svg: FIG_SOFTPHONE, alt: 'Panneau téléphone avec clavier et bouton Appeler', caption: 'Le softphone : composez un numéro puis cliquez sur Appeler.' },
        { type: 'h', text: 'Les commandes' },
        { type: 'kv', items: [
          { k: 'Clavier', v: "Tapez un numéro puis **Appeler**. Vous pouvez coller un numéro." },
          { k: 'Mute (micro)', v: "Coupe votre micro — le contact ne vous entend plus, vous l'entendez toujours." },
          { k: 'Attente (hold)', v: "Met l'appel en pause (le contact patiente)." },
          { k: 'Transfert', v: "Renvoie l'appel vers un collègue ou un autre numéro. Voir [[appels/transfert|Transférer un appel]]." },
          { k: 'Raccrocher', v: "Termine l'appel. La **fiche de clôture** (disposition) s'ouvre alors — voir [[dispositions|Qualifier un appel]]." },
        ] },
        { type: 'callout', variant: 'warning', title: 'Un appel à la fois', text: "Le bouton Appeler est bloqué tant qu'un appel est en cours (le softphone est « occupé »). Terminez d'abord l'appel courant." },
        { type: 'h', text: 'Replier le panneau' },
        { type: 'p', text: "Vous pouvez **replier tout le panneau téléphone** (comme le menu de gauche) pour gagner de la place à l'écran : cliquez sur la flèche dans l'en-tête du softphone. Une fine bande « Téléphone » reste sur le bord droit — cliquez dessus pour le rouvrir." },
        { type: 'callout', variant: 'tip', title: 'Toujours prêt même replié', text: "Replié, le téléphone **reste connecté** : vous ne ratez aucun appel. Il se **rouvre automatiquement** dès qu'un appel arrive ou démarre. Votre choix (replié/ouvert) est mémorisé." },
      ],
    },
    {
      id: 'composer',
      title: 'Composer un appel (mode aperçu)',
      summary: 'Appeler un numéro de votre file ou un numéro libre.',
      icon: 'phone-outgoing',
      blocks: [
        { type: 'p', text: "En mode **aperçu** (le mode par défaut), **c'est vous qui lancez l'appel** : vous voyez d'abord la fiche, puis vous cliquez sur Appeler. Vous êtes en ligne dès le départ." },
        { type: 'h', text: 'Depuis votre file' },
        { type: 'steps', items: [
          "Ouvrez **Espace d'appel** (ou une campagne dans la barre latérale).",
          "Sélectionnez un numéro : sa fiche se prépare dans le softphone.",
          "Vérifiez le contact, puis cliquez **Appeler**.",
          "À la fin, **qualifiez** l'appel (disposition) pour passer au suivant.",
        ] },
        { type: 'h', text: 'Un numéro libre (hors file)' },
        { type: 'p', text: "Tapez directement le numéro sur le clavier du softphone et appelez. PexiTalk crée une **fiche d'appel manuel** : vous avez le même espace de travail (notes, qualification, identification du contact s'il est connu)." },
        { type: 'callout', variant: 'info', title: 'Quel numéro s\'affiche chez le contact ?', text: "Le numéro présenté est choisi automatiquement (selon la campagne, vous, l'équipe ou le réglage du compte). Vous n'avez rien à faire. Les responsables peuvent le configurer — voir [[telephonie/caller-id|Numéro présenté]]." },
        { type: 'callout', variant: 'warning', title: 'Numéro « client »', text: "Si un numéro est marqué comme appartenant à un de vos clients, une **confirmation** peut s'afficher avant l'appel (lorsque la campagne a activé cet avertissement)." },
      ],
    },
    {
      id: 'recevoir',
      title: 'Recevoir un appel entrant',
      summary: 'Comment les appels entrants arrivent jusqu\'à vous.',
      icon: 'phone-incoming',
      blocks: [
        { type: 'p', text: "Quand un client appelle un de vos **numéros entrants (DID)**, PexiTalk cherche **qui est disponible** pour le prendre, puis fait sonner le ou les agents concernés." },
        { type: 'steps', items: [
          "Vous devez être en statut **Disponible** pour être sonné.",
          "Votre softphone sonne : décrochez depuis le panneau.",
          "La fiche du contact s'affiche (PexiTalk l'identifie s'il est déjà connu : carnet, campagne, RDV).",
          "À la fin, qualifiez l'appel comme pour un sortant.",
        ] },
        { type: 'callout', variant: 'info', title: 'Qui sonne ?', text: "La distribution dépend du **groupe d'agents (ring group)** rattaché au numéro et de la stratégie choisie (tous en même temps, le plus inactif, à tour de rôle…). C'est configuré par un administrateur — voir [[telephonie/distribution|Distribution des appels entrants]]." },
        { type: 'callout', variant: 'warning', title: 'Personne de disponible', text: "Si aucun agent n'est Disponible, l'appel bascule sur le repli prévu (souvent une messagerie). Restez en Disponible pour ne pas manquer d'appels." },
      ],
    },
    {
      id: 'pendant',
      title: "Pendant l'appel : fiche, script, notes",
      summary: "Tout ce que vous avez sous les yeux quand vous parlez au contact.",
      icon: 'file-text',
      blocks: [
        { type: 'p', text: "Dès que l'appel démarre, l'**espace de travail** s'ouvre au centre. Vous y trouvez tout pour mener l'entretien." },
        { type: 'kv', items: [
          { k: 'Fiche contact', v: "Nom, entreprise, ville, e-mail, secteur… Vous pouvez **corriger** ces champs en direct — chaque modification est tracée (qui, quand, ancienne→nouvelle valeur)." },
          { k: 'Script', v: "Le texte d'appel défini par la campagne s'affiche pour vous guider. Voir [[dispositions|Qualifier un appel]] pour la suite." },
          { k: 'Champs personnalisés', v: "Des champs propres à la campagne (ex. « budget », « date de contrat ») que vous remplissez et qui sont enregistrés sur la fiche." },
          { k: 'Notes', v: "Une zone de notes libre, conservée sur la fiche et visible dans l'historique." },
          { k: 'Rendez-vous', v: "Si vous obtenez un RDV, un formulaire dédié peut s'afficher — voir [[rappels/rdv|Prendre un rendez-vous]]." },
        ] },
        { type: 'callout', variant: 'tip', title: 'Enregistrement', text: "Les appels sont **enregistrés** si la campagne l'autorise. Vous retrouvez l'audio dans l'[[historique|Historique]]. Les appels « Vente » et « RDV » sont conservés plus longtemps." },
      ],
    },
    {
      id: 'lignes',
      title: 'Choisir sa ligne sortante (page Lignes)',
      summary: "Par quelle ligne (opérateur SIP) partent vos appels.",
      icon: 'phone-outgoing',
      blocks: [
        { type: 'p', text: "Si plusieurs **lignes** (opérateurs SIP) vous sont assignées, la page **Lignes** apparaît dans la barre latérale : elle vous laisse choisir la ligne **active**, celle par laquelle partent vos appels sortants." },
        { type: 'steps', items: [
          "Ouvrez **Lignes** dans la barre latérale (visible seulement si vous avez au moins 2 lignes).",
          "Cliquez sur la carte de la ligne à utiliser : elle passe **Active**.",
          "Vos prochains appels sortants partent par cette ligne.",
        ] },
        { type: 'callout', variant: 'info', title: 'Une seule ligne ?', text: "Avec une seule ligne assignée, il n'y a rien à choisir : la page ne s'affiche pas dans le menu et vos appels partent automatiquement par elle. L'assignation des lignes est gérée par un responsable." },
        { type: 'callout', variant: 'warning', title: 'Ligne de secours', text: "Si votre ligne **tombe en panne** (opérateur qui refuse la connexion ou rejette les appels) et qu'un responsable vous a **autorisé la bascule**, vos prochains appels partent **automatiquement** par la **ligne de secours** (le SIP par défaut du compte). Vous êtes prévenu par une **notification** au lancement de l'appel et un badge **« secours »** (ambre) s'affiche dans l'en-tête du softphone pendant l'appel. Le numéro présenté au contact est alors l'un de ceux définis pour cette ligne (sinon le numéro par défaut du compte). Tout revient à la normale dès que votre ligne refonctionne — vous n'avez rien à faire." },
      ],
    },
    {
      id: 'transfert',
      title: 'Transférer un appel',
      summary: 'Renvoyer un appel vers un collègue ou un numéro externe.',
      icon: 'corner-down-right',
      blocks: [
        { type: 'p', text: "Le **transfert** renvoie l'appel en cours vers une autre destination. Deux formes :" },
        { type: 'kv', items: [
          { k: 'Transfert aveugle', v: "Vous renvoyez immédiatement l'appel vers un collègue/numéro et vous raccrochez. Le contact est mis en relation directement." },
          { k: 'Transfert accompagné', v: "Vous appelez d'abord le collègue pour lui présenter l'appel, puis vous connectez les deux. (Selon votre installation.)" },
        ] },
        { type: 'steps', items: [
          "Pendant l'appel, ouvrez le **transfert** dans le softphone.",
          "Choisissez le collègue (par son poste) ou tapez un numéro externe.",
          "Confirmez. Pour un transfert accompagné, parlez au collègue avant de finaliser.",
        ] },
        { type: 'callout', variant: 'info', text: "Le **répertoire interne** (barre latérale) liste les postes de vos collègues pour les appels internes et les transferts." },
      ],
    },
  ],
}
